<template>
    <div class="w-full px-4 py-8 min-h-[570px]">
        <!-- Desktop: Animated stacked cards -->
        <div class="hidden lg:block relative w-full mx-auto max-w-2xl">
            <div v-for="(card, index) in cards" :key="card.id"
                class="absolute top-0 left-0 bg-slate-800/50 rounded-3xl py-8 px-6 backdrop-blur-md border border-white/20 w-full transition-all duration-700 ease-in-out cursor-pointer hover:shadow-2xl"
                :style="getCardStyle(index)" @click="bringCardToFront(index)">
                <NsPerformanceCards :card="card" />
            </div>
        </div>

        <!-- Tablet: Grid layout -->
        <div class="hidden md:grid lg:hidden grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div v-for="card in cards" :key="card.id"
                class="bg-slate-800/50 rounded-2xl p-6 backdrop-blur-md border border-white/20 transition-transform duration-300 hover:scale-[1.02]">
                <NsPerformanceCards :card="card" :is-compact="true" />
            </div>
        </div>

        <!-- Mobile: Single column -->
        <div class="md:hidden space-y-6 max-w-sm mx-auto">
            <div v-for="card in cards" :key="card.id"
                class="bg-slate-800/50 rounded-2xl p-4 backdrop-blur-md border border-white/20 transition-transform duration-300">
                <NsPerformanceCards :card="card" :is-mobile="true" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'

const NsPerformanceCards = defineAsyncComponent(() => import('~/components/NsPerformanceCards.vue'))

interface Metric {
    key: string;
    label: string;
    value: string;
}

interface Card {
    id: string;
    title: string;
    subtitle: string;
    score: number;
    metricsTitle: string;
    metrics: Metric[];
}

const cards: Card[] = [
    {
        id: 'performance',
        title: 'Performance',
        subtitle: 'Core Web Vitals',
        score: 98,
        metricsTitle: 'Core Metrics',
        metrics: [
            { key: 'fcp', label: 'First Contentful Paint', value: '1.9 s' },
            { key: 'lcp', label: 'Largest Contentful Paint', value: '2.4 s' },
            { key: 'tbt', label: 'Total Blocking Time', value: '430 ms' },
            { key: 'cls', label: 'Cumulative Layout Shift', value: '0.119' },
            { key: 'si', label: 'Speed Index', value: '2.2 s' },
            { key: 'tti', label: 'Time to Interactive', value: '3.1 s' }
        ]
    },
    {
        id: 'accessibility',
        title: 'Accessibility',
        subtitle: 'WCAG Compliance',
        score: 94,
        metricsTitle: 'Accessibility Checks',
        metrics: [
            { key: 'color_contrast', label: 'Color Contrast', value: 'AAA' },
            { key: 'alt_text', label: 'Alt Text Coverage', value: '100%' },
            { key: 'keyboard_nav', label: 'Keyboard Navigation', value: 'Perfect' },
            { key: 'aria_labels', label: 'ARIA Labels', value: '98%' },
            { key: 'focus_mgmt', label: 'Focus Management', value: 'Good' }
        ]
    },
    {
        id: 'best-practices',
        title: 'Best Practices',
        subtitle: 'Code Quality',
        score: 100,
        metricsTitle: 'Quality Metrics',
        metrics: [
            { key: 'https', label: 'HTTPS Usage', value: 'Secure' },
            { key: 'console_errors', label: 'Console Errors', value: '0' },
            { key: 'deprecated_apis', label: 'Deprecated APIs', value: 'None' },
            { key: 'security_headers', label: 'Security Headers', value: 'Present' },
            { key: 'image_optimization', label: 'Image Optimization', value: 'Perfect' }
        ]
    },
    {
        id: 'seo',
        title: 'SEO',
        subtitle: 'Search Optimization',
        score: 92,
        metricsTitle: 'SEO Factors',
        metrics: [
            { key: 'meta_tags', label: 'Meta Tags', value: '95%' },
            { key: 'structured_data', label: 'Structured Data', value: 'Present' },
            { key: 'mobile_friendly', label: 'Mobile Friendly', value: 'Yes' },
            { key: 'crawlability', label: 'Crawlability', value: 'Good' },
            { key: 'site_speed', label: 'Site Speed', value: 'Fast' }
        ]
    }
];

// Animation state
const currentCardIndex = ref(0)
const animationInterval = ref<NodeJS.Timeout | null>(null)
const isHovered = ref(false)

// Auto-animation settings
const ANIMATION_DELAY = 4000 // 4 seconds per card
const PAUSE_ON_HOVER = true

// Calculate card positions and z-index

// Calculate card positions and z-index with progressive scaling
const getCardStyle = (index: number) => {
    const adjustedIndex = (index - currentCardIndex.value + cards.length) % cards.length
    const isTopCard = adjustedIndex === 0

    // Progressive scaling: each card gets smaller
    const scaleValue = 1 - (adjustedIndex * 0.03) // 0.97, 0.94, 0.91, etc.

    return {
        transform: `translateX(${adjustedIndex * 40}px) translateY(${adjustedIndex * 2}px) scale(${scaleValue})`,
        zIndex: cards.length - adjustedIndex,
        opacity: adjustedIndex > 2 ? 0.6 : 1,
        filter: adjustedIndex > 0 ? 'brightness(0.85)' : 'brightness(1)'
    }
}

// Navigation functions
const nextCard = () => {
    currentCardIndex.value = (currentCardIndex.value + 1) % cards.length
}

const previousCard = () => {
    currentCardIndex.value = (currentCardIndex.value - 1 + cards.length) % cards.length
}

const bringCardToFront = (index: number) => {
    currentCardIndex.value = index
    resetAnimation()
}

// Animation control
const startAnimation = () => {
    if (animationInterval.value) {
        clearInterval(animationInterval.value)
    }

    animationInterval.value = setInterval(() => {
        if (!isHovered.value || !PAUSE_ON_HOVER) {
            nextCard()
        }
    }, ANIMATION_DELAY)
}

const stopAnimation = () => {
    if (animationInterval.value) {
        clearInterval(animationInterval.value)
        animationInterval.value = null
    }
}

const resetAnimation = () => {
    stopAnimation()
    startAnimation()
}

// Mouse events for pausing animation
const handleMouseEnter = () => {
    isHovered.value = true
}

const handleMouseLeave = () => {
    isHovered.value = false
}

// Lifecycle
onMounted(() => {
    startAnimation()

    // Add event listeners for hover detection
    const cardContainer = document.querySelector('.lg\\:block')
    if (cardContainer && PAUSE_ON_HOVER) {
        cardContainer.addEventListener('mouseenter', handleMouseEnter)
        cardContainer.addEventListener('mouseleave', handleMouseLeave)
    }
})

onUnmounted(() => {
    stopAnimation()

    // Clean up event listeners
    const cardContainer = document.querySelector('.lg\\:block')
    if (cardContainer && PAUSE_ON_HOVER) {
        cardContainer.removeEventListener('mouseenter', handleMouseEnter)
        cardContainer.removeEventListener('mouseleave', handleMouseLeave)
    }
})

// Keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
        previousCard()
        resetAnimation()
    } else if (event.key === 'ArrowRight') {
        nextCard()
        resetAnimation()
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Add smooth transitions for card animations */
.card-stack {
    perspective: 1000px;
}

/* Enhance the hover effects */
.group:hover .opacity-0 {
    opacity: 1;
}

/* Add subtle animation for indicators */
@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.5;
    }
}

.animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>